import { RequirePermission } from "@/hooks/shared/CheckAuth";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

import { EmptyState } from "@/components/dashboard/data-table/empty-state";
import { FilterBar } from "@/components/dashboard/data-table/filter-bar";
import { Pagination } from "@/components/dashboard/data-table/pagination";
import { QueryState } from "@/components/dashboard/query-state";
import {
  TBody,
  TD,
  TEmpty,
  TH,
  THead,
  TR,
  Table,
  TableShell,
} from "@/components/dashboard/data-table/table";
import { PageHeader } from "@/components/dashboard/page-header";
import { StatusPill } from "@/components/dashboard/status-pill";
import { audit as copy, nav } from "@/content";
import { useDocumentTitle } from "@/hooks/shared/useDocumentTitle";
import { useFmt } from "@/hooks/shared/useFmt";
import { useQueryParams } from "@/hooks/shared/useQueryParams";
import { useSession } from "@/context/UserContext";
import { readEnum, readNumber, readPage, readPageSize, readSearch } from "@/utils/query-params";
import { AUDIT_ACTIONS, auditFilterOptions, listAudit } from "@/api/system";

const BASE_PATH = "/dashboard/audit";

/**
 * The audit log.
 *
 * The prototype had none, and no soft deletes either — so "who approved this leave
 * request", "who deactivated this guard" and "who moved this guard's zone" were
 * all unanswerable. That matters more here than in most products: a security
 * contractor's operating licence rests on demonstrable control of its staffing,
 * and an attendance record nobody can vouch for is worth very little in a dispute.
 *
 * Admin-only, and deliberately so: a log everyone can read is a log people are
 * careful in front of. The route guard enforces it; this screen assumes it.
 */
export const Route = createFileRoute("/_main/dashboard/audit")({
  component: () => (
    <RequirePermission action="audit.read">
      <AuditPage />
    </RequirePermission>
  ),
});

function AuditPage() {
  useDocumentTitle(copy.title);

  const params = useQueryParams();
  const fmt = useFmt();
  const { user } = useSession();

  const filters = {
    search: readSearch(params),
    actorId: readNumber(params, "actor"),
    action: readEnum(params, "action", AUDIT_ACTIONS),
    entity: typeof params.entity === "string" ? params.entity : undefined,
    page: readPage(params),
    pageSize: readPageSize(params),
  };

  /* Keyed on the filters, so paging or changing a select is a new query rather
     than a manual refetch — and going back to a page already seen is instant. */
  const rows = useQuery({
    queryKey: ["audit", "list", filters],
    queryFn: () => listAudit(filters),
  });

  /* The filter options are their own query: they change when a new table starts
     writing audit rows, not when the reader pages through. */
  const options = useQuery({
    queryKey: ["audit", "options"],
    queryFn: auditFilterOptions,
    staleTime: 5 * 60_000,
  });

  const isFiltered = Boolean(
    readSearch(params) || params.actor || params.action || params.entity,
  );

  const actionLabel = {
    create: copy.actionCreate,
    update: copy.actionUpdate,
    delete: copy.actionDelete,
    restore: copy.actionRestore,
  } as const;

  return (
    <div className="mx-auto w-full max-w-shell px-6 py-8">
      <PageHeader eyebrow={nav.groups.system} title={copy.title} lead={copy.lead} />

      <div className="mt-6">
        <FilterBar
          params={params}
          basePath={BASE_PATH}
          searchPlaceholder={copy.searchPlaceholder}
          selects={[
            {
              key: "actor",
              label: copy.filterActor,
              value: typeof params.actor === "string" ? params.actor : "",
              options: (options.data?.actors ?? []).map((actor) => ({
                value: String(actor.id),
                label: actor.name,
              })),
            },
            {
              key: "action",
              label: copy.filterAction,
              value: typeof params.action === "string" ? params.action : "",
              options: AUDIT_ACTIONS.map((action) => ({
                value: action,
                label: actionLabel[action],
              })),
            },
            {
              key: "entity",
              label: copy.filterEntity,
              value: typeof params.entity === "string" ? params.entity : "",
              /* Straight from the log rather than from a maintained list: a new
                 audited table appears in the filter the moment it writes a row. */
              options: (options.data?.entities ?? []).map((entity) => ({
                value: entity,
                label: entity,
              })),
            },
          ]}
        />
      </div>

      <QueryState isPending={rows.isPending} isError={rows.isError && !rows.data} error={rows.error}>
        <TableShell className="mt-5">
          <Table>
            <THead>
              <TH>{copy.columnWhen}</TH>
              <TH>{copy.columnActor}</TH>
              <TH>{copy.columnAction}</TH>
              <TH>{copy.columnEntity}</TH>
              <TH>{copy.columnChange}</TH>
              <TH>{copy.columnIp}</TH>
            </THead>

            <TBody>
              {(rows.data?.rows.length ?? 0) === 0 ? (
                <TEmpty colSpan={6}>
                  <EmptyState
                    variant={isFiltered ? "filtered" : "empty"}
                    title={isFiltered ? copy.emptyFilteredTitle : copy.emptyTitle}
                    lead={isFiltered ? copy.emptyFilteredLead : copy.emptyLead}
                    actionHref={isFiltered ? BASE_PATH : undefined}
                    actionLabel={isFiltered ? copy.clearFilters : undefined}
                  />
                </TEmpty>
              ) : (
                rows.data?.rows.map((row) => (
                  <TR key={row.id}>
                    <TD numeric>
                      <span className="tabular text-xs">{fmt.dateTime(row.createdAt)}</span>
                    </TD>

                    <TD>
                      {/* A null actor is a system action — the retention sweep,
                          for instance — and those are exactly the rows you least
                          want dropped from the log by an inner join. */}
                      <span className="text-xs font-semibold text-n8">
                        {row.actorName ?? copy.system}
                      </span>
                    </TD>

                    <TD>
                      {row.action === "delete" ? (
                        <StatusPill tone="negative">{actionLabel.delete}</StatusPill>
                      ) : row.action === "create" ? (
                        <StatusPill tone="positive">{actionLabel.create}</StatusPill>
                      ) : (
                        <StatusPill tone="neutral">{actionLabel[row.action]}</StatusPill>
                      )}
                    </TD>

                    <TD>
                      <span className="text-xs text-n7" dir="ltr">
                        {row.entity}
                        {row.entityId !== null ? `#${row.entityId}` : ""}
                      </span>
                    </TD>

                    <TD>
                      {/* Only the keys that changed, which is what makes a diff
                          readable rather than a wall of unchanged columns.
                          `recordAudit` redacts password and token hashes before
                          they ever reach a row. */}
                      {row.before === null && row.after === null ? (
                        <span className="text-[11px] text-n6">{copy.noChange}</span>
                      ) : (
                        <details className="group">
                          <summary className="cursor-pointer list-none text-[11px] font-semibold text-orange-700 transition-opacity duration-200 ease-brand hover:opacity-80 [&::-webkit-details-marker]:hidden">
                            {copy.showChange}
                          </summary>
                          <dl className="mt-2 flex flex-col gap-1.5">
                            {Object.keys({ ...row.before, ...row.after }).map((key) => (
                              <div key={key} className="text-[11px] leading-snug">
                                <dt className="font-semibold text-n7" dir="ltr">
                                  {key}
                                </dt>
                                {/* Named as well as coloured. An arrow between two
                                    tinted values is not a signal to anyone reading
                                    with a screen reader or without colour. */}
                                <dd className="tabular text-n6" dir="ltr">
                                  <span className="sr-only">{copy.before}: </span>
                                  <span className="text-destructive">
                                    {formatValue(row.before?.[key])}
                                  </span>
                                  {" → "}
                                  <span className="sr-only">{copy.after}: </span>
                                  <span className="text-n8">{formatValue(row.after?.[key])}</span>
                                </dd>
                              </div>
                            ))}
                          </dl>
                        </details>
                      )}
                    </TD>

                    <TD>
                      <span className="tabular text-[11px] text-n6" dir="ltr">
                        {row.ip ?? "—"}
                      </span>
                    </TD>
                  </TR>
                ))
              )}
            </TBody>
          </Table>

          {rows.data && rows.data.rows.length > 0 ? (
            <Pagination
              page={rows.data.page}
              pageCount={rows.data.pageCount}
              pageSize={rows.data.pageSize}
              total={rows.data.total}
              params={params}
              basePath={BASE_PATH}
              numerals={user.numeralSystem}
            />
          ) : null}
        </TableShell>
      </QueryState>
    </div>
  );
}

/**
 * A stored value, as text.
 *
 * Deliberately plain and Latin-digit: this is the raw record, and a reader
 * comparing it against the database wants exactly what is in the column — not a
 * localised rendering of it.
 */
function formatValue(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") return value ? "true" : "false";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}
